import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { getIsValidAuthNumber } from 'shared/helpers/getIsValidAuthNumber'
import type { IDropdownItem } from '../../Dropdown'
import { CountryEnum } from '../types'

export class PhoneInputStore {
  private _disposeNumberValidation: IReactionDisposer | null = null
  private _numberError: string | null = null
  private _numberRequestError: string | null = null
  private _numberValidationTrigger: string | null = null
  private _number = ''
  private _country: IDropdownItem
  private _excludeCountries: CountryEnum[] = []

  constructor({
    excludeCountries,
    number,
  }: { excludeCountries?: CountryEnum[]; number?: string } = {}) {
    if (excludeCountries) this._excludeCountries = excludeCountries
    if (number) this._number = number
    this._country = this.defaultCountry
    makeAutoObservable(this)

    this.reactionNumberValidation()
  }

  isValidCountryEnum = (id: string | number): id is CountryEnum => {
    if (typeof id !== 'string') return false

    const validCountries: string[] = Object.values(CountryEnum)

    return validCountries.includes(id)
  }

  get number() {
    return this._number
  }

  get fullNumber() {
    return `${this.country.labelRight}${this._number}`
  }

  get fullUnformattedNumber() {
    return `${this.country.labelRight}${this._number.replace(/[^0-9]/g, '')}`
  }

  get country() {
    return this._country
  }

  get isValidNumber() {
    let countryCode

    if (this.isValidCountryEnum(this.country.id)) countryCode = this.country.id

    return getIsValidAuthNumber(this.number, countryCode)
  }

  get numberError() {
    return this._numberError
  }

  get numberRequestError() {
    return this._numberRequestError
  }

  get USCountry(): IDropdownItem {
    return {
      label: 'United States',
      id: CountryEnum.US,
      iconL: 'flagUSA',
      labelRight: '+1',
    }
  }

  get defaultCountry(): IDropdownItem {
    return this.countries?.[0] ?? this.USCountry
  }

  get countries(): IDropdownItem[] {
    const _countries: IDropdownItem[] = [
      this.USCountry,
      {
        label: 'Canada',
        id: CountryEnum.CA,
        iconL: 'flagCanada',
        labelRight: '+1',
      },
      {
        label: 'United Kingdom',
        id: CountryEnum.GB,
        iconL: 'flagUK',
        labelRight: '+44',
      },
      {
        label: 'Australia',
        id: CountryEnum.AU,
        iconL: 'flagAU',
        labelRight: '+61',
      },
    ]

    return this._excludeCountries && this._excludeCountries.length > 0
      ? _countries.filter((country) => !this._excludeCountries.includes(country.id as CountryEnum))
      : _countries
  }

  triggerNumberValidation = () => {
    this._numberValidationTrigger = nanoid()
  }

  setNumber = (value: string) => {
    if (this._numberRequestError) this.setNumberRequestError('')
    this._number = value
  }

  setNumberError = (value: string) => {
    this._numberError = value
  }

  setNumberRequestError = (value: string) => {
    this._numberRequestError = value
  }

  setCountry = (value: IDropdownItem) => {
    this._country = value

    this.checkValidity(value)
  }

  checkValidity(value: IDropdownItem) {
    const cleanedNumber = this._number.replace(/\D/g, '')
    const newMaxLength = this.isValidCountryEnum(value.id) && value.id === CountryEnum.AU ? 9 : 10

    if (cleanedNumber.length > newMaxLength) {
      this._number = cleanedNumber.slice(0, newMaxLength)
    }

    if (this._number) {
      this.triggerNumberValidation()
    }
  }

  setCountryById = (value: string) => {
    const filteredCountry = this.countries.filter((el) => el.id === value)

    if (!filteredCountry.length) return

    this._country = filteredCountry[0]
  }

  setExcludeCountries = (countries: CountryEnum[]) => {
    this._excludeCountries = countries
  }

  reset = () => {
    this._number = ''
    this._numberError = null
    this._numberRequestError = null
    this._numberValidationTrigger = null
    this._country = this.defaultCountry
  }

  reactionNumberValidation = () => {
    this._disposeNumberValidation?.()
    this._disposeNumberValidation = reaction(
      () => [this._numberValidationTrigger, this.isValidNumber],
      ([trigger, isValid]) => {
        this._numberError = trigger && !isValid ? 'Please enter a valid phone number' : null
        if (isValid) this._numberValidationTrigger = null
      },
      {
        fireImmediately: true,
      }
    )
  }
}
